import { HelpCircle } from 'lucide-react';
import { Textarea } from '../ui/textarea';

/**
 * "Can't find what you need?" — shared component shown once at the bottom
 * of the first service-selection screen for every category.
 *
 * The customer can describe a service that isn't in the predefined list and
 * still submit a valid lead. The value is tracked in a dedicated field
 * (`unlistedServiceDescription`) so it never overwrites the normal
 * `additionalDetails` textarea shown near the end of the flow.
 *
 * @param {string} value
 * @param {(next: string) => void} onChange
 * @param {number} [maxLength=1000]
 */
export const CustomServiceRequest = ({
  value = '',
  onChange,
  maxLength = 1000
}) => {
  const currentLength = (value || '').length;
  const isAtLimit = currentLength >= maxLength;

  const handleChange = (e) => {
    const next = e.target.value;
    if (next.length <= maxLength) {
      onChange(next);
    }
  };

  return (
    <div
      className="mt-6 p-4 bg-[#0FA3B1]/5 border border-[#0FA3B1]/20 rounded-xl"
      data-testid="custom-service-request"
    >
      <div className="flex items-center gap-2 mb-1">
        <HelpCircle className="w-4 h-4 text-[#0FA3B1]" />
        <h3 className="font-semibold text-[#1F2933] text-sm">
          Can't find exactly what you need?
        </h3>
      </div>
      <p className="text-xs text-[#1F2933]/60 mb-3">
        Tell us what you need and we'll still help connect you with the right contractor.
      </p>

      <div className="relative">
        <Textarea
          value={value || ''}
          onChange={handleChange}
          placeholder="Describe the work you need, the problem you're having, or anything you couldn't find in the options above."
          maxLength={maxLength}
          className="bg-white border-gray-200 text-[#1F2933] resize-none pr-16 min-h-[100px]"
          data-testid="unlisted-service-input"
        />
        <span
          className={`absolute bottom-3 right-3 text-xs ${
            isAtLimit ? 'text-red-500' : 'text-gray-400'
          }`}
        >
          {currentLength}/{maxLength}
        </span>
      </div>
    </div>
  );
};

export default CustomServiceRequest;
